import { useState, useMemo } from 'react';
import { helpContent } from '../lib/help-content';
import type { HelpItem } from '../types';
import { Search, X, ChevronLeft, ImageOff, HelpCircle } from 'lucide-react';

interface HelpCenterProps {
  onBack: () => void;
}

export default function HelpCenter({ onBack }: HelpCenterProps) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<HelpItem | null>(null);
  const [imageError, setImageError] = useState(false);

  const items = useMemo(() => Object.values(helpContent), []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return items.filter((h) =>
      h.title.toLowerCase().includes(q) ||
      h.definition.toLowerCase().includes(q) ||
      h.howTo.toLowerCase().includes(q)
    );
  }, [items, search]);

  return (
    <div className="space-y-6 animate-fade-in">
      <button type="button" onClick={onBack} className="text-sm text-navy-400 hover:text-navy-200 flex items-center gap-1">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <div className="glass rounded-xl p-4 flex items-center gap-3">
        <HelpCircle className="w-5 h-5 text-ocean-400" />
        <div>
          <h2 className="text-lg font-bold text-navy-100">Help Center</h2>
          <p className="text-xs text-navy-400">Search terms, definitions, and measurement guides</p>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-500" />
        <input
          type="text"
          placeholder="Search draft survey terms..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input-premium pl-10"
        />
        {search && (
          <button type="button" onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-navy-500 hover:text-navy-300">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {filtered.map((h) => (
          <button type="button"
            key={h.id}
            onClick={() => { setSelected(h); setImageError(false); }}
            className="card-premium-hover p-4 text-left"
          >
            <h3 className="font-bold text-navy-100 text-sm mb-1">{h.title}</h3>
            <p className="text-xs text-navy-400 line-clamp-2">{h.definition}</p>
          </button>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12">
          <Search className="w-12 h-12 text-navy-700 mx-auto mb-3" />
          <p className="text-navy-400 text-sm">No results for "{search}"</p>
        </div>
      )}

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-navy-950/80 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <div className="relative w-full sm:max-w-lg bg-navy-800 sm:rounded-2xl rounded-t-2xl shadow-2xl border border-navy-700 max-h-[85vh] sm:max-h-[90vh] overflow-y-auto animate-slide-up">
            <div className="sticky top-0 bg-navy-800/95 backdrop-blur-sm border-b border-navy-700 px-4 py-3 flex items-center justify-between z-10">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-ocean-600/20 flex items-center justify-center">
                  <HelpCircle className="w-4 h-4 text-ocean-400" />
                </div>
                <h2 className="text-base font-bold text-navy-100">{selected.title}</h2>
              </div>
              <button type="button" onClick={() => setSelected(null)} className="p-2 hover:bg-navy-700 rounded-lg transition-colors">
                <X className="w-5 h-5 text-navy-400" />
              </button>
            </div>

            <div className="p-4 space-y-5">
              <div className="rounded-xl overflow-hidden border border-navy-700 bg-navy-900/50">
                {!imageError ? (
                  <img
                    src={selected.imageUrl}
                    alt={selected.title}
                    className="w-full h-48 sm:h-56 object-contain bg-navy-900"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="h-48 sm:h-56 flex flex-col items-center justify-center text-navy-500 gap-2">
                    <ImageOff className="w-10 h-10" />
                    <p className="text-sm">Image unavailable</p>
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-xs font-bold text-ocean-400 uppercase tracking-wider mb-2">Definition</h3>
                <p className="text-sm text-navy-300 leading-relaxed">{selected.definition}</p>
              </div>

              <div className="bg-ocean-600/5 border border-ocean-600/10 rounded-xl p-4">
                <h3 className="text-xs font-bold text-ocean-400 uppercase tracking-wider mb-2">How to Measure</h3>
                <p className="text-sm text-navy-200 leading-relaxed">{selected.howTo}</p>
              </div>

              {selected.related && selected.related.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold text-navy-500 uppercase tracking-wider mb-2">Related Terms</h3>
                  <div className="flex flex-wrap gap-2">
                    {selected.related.map((r) => (
                      <span key={r} className="px-3 py-1.5 bg-navy-700/50 text-navy-300 text-xs rounded-lg border border-navy-700">
                        {r}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-navy-700 bg-navy-800/50">
              <button type="button"
                onClick={() => setSelected(null)}
                className="w-full py-3 bg-ocean-600 hover:bg-ocean-500 text-white rounded-xl font-semibold transition-all active:scale-[0.98]"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
